import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { getJournalDetails, clearJournalDetails } from '../../../../redux/actions/SocietyPortal/JournalAction/JournalAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';

const ENTRY_TYPE_LABELS = {
    payment: 'Payment',
    receipt: 'Receipt',
    daily: 'Daily',
    opening: 'Opening',
    closing: 'Closing',
    debitnote: 'Debit Note',
    creditnote: 'Credit Note',
    advance: 'Advance',
    invoice: 'Invoice',
    debit_receipt: 'Debit Receipt',
    facility: 'Facility',
    facility_receipt: 'Facility Receipt',
    expense: 'Expense',
    other: 'Other',
};

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ViewJournalModal = ({ journalId, onClose }) => {
    const dispatch = useDispatch();
    const { journalDetails: entry, detailsLoading } = useSelector(state => state.JournalReducer);
    const societyId = selectedSocietyStorer.getSocietyId();

    useEffect(() => {
        if (journalId && societyId) {
            dispatch(getJournalDetails({ societyId, journalId }));
        }
        return () => {
            dispatch(clearJournalDetails());
        };
    }, [dispatch, journalId, societyId]);

    if (!journalId) return null;

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-2xl tw-mx-4 tw-max-h-[85vh] tw-overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:file-document-outline" className="tw-text-blue-500" />
                        <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">Journal Entry #{journalId}</h2>
                        {!detailsLoading && entry && (
                            <span className={`tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full ${entry.isBalanced ? 'tw-bg-emerald-50 tw-text-emerald-700' : 'tw-bg-red-50 tw-text-red-600'}`}>
                                <Icon icon={entry.isBalanced ? 'mdi:check-circle-outline' : 'mdi:alert-circle-outline'} className="tw-text-xs tw-inline tw-mr-0.5 tw-align-middle" />
                                {entry.isBalanced ? 'Balanced' : 'Unbalanced'}
                            </span>
                        )}
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600">
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                {/* Body */}
                {detailsLoading ? (
                    <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-gray-300 tw-border-t-blue-500"></div>
                            <span className="tw-text-gray-500 tw-text-sm">Loading journal details...</span>
                        </div>
                    </div>
                ) : entry ? (
                    <div className="tw-px-5 tw-py-4">
                        {/* Info Grid */}
                        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-mb-4">
                            <div>
                                <p className="tw-text-[10px] tw-text-gray-600 tw-font-medium tw-uppercase tw-tracking-wide tw-flex tw-items-center tw-gap-1">
                                    <Icon icon="mdi:calendar" className="tw-text-xs" /> Date
                                </p>
                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-mt-0.5">{entry.date}</p>
                            </div>
                            <div>
                                <p className="tw-text-[10px] tw-text-gray-600 tw-font-medium tw-uppercase tw-tracking-wide tw-flex tw-items-center tw-gap-1">
                                    <Icon icon="mdi:tag-outline" className="tw-text-xs" /> Entry Type
                                </p>
                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-mt-0.5">{ENTRY_TYPE_LABELS[entry.entry_type] || entry.entry_type}</p>
                            </div>
                            <div>
                                <p className="tw-text-[10px] tw-text-gray-600 tw-font-medium tw-uppercase tw-tracking-wide tw-flex tw-items-center tw-gap-1">
                                    <Icon icon="mdi:file-document-outline" className="tw-text-xs" /> Reference No.
                                </p>
                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-mt-0.5">{entry.reff_no || '-'}</p>
                            </div>
                            <div>
                                <p className="tw-text-[10px] tw-text-gray-600 tw-font-medium tw-uppercase tw-tracking-wide tw-flex tw-items-center tw-gap-1">
                                    <Icon icon="mdi:flag-outline" className="tw-text-xs" /> Flag
                                </p>
                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-mt-0.5">{entry.entry_flag || '-'}</p>
                            </div>
                        </div>

                        {/* Remarks */}
                        <div className="tw-mb-4">
                            <p className="tw-text-[10px] tw-text-gray-600 tw-font-medium tw-uppercase tw-tracking-wide tw-flex tw-items-center tw-gap-1">
                                <Icon icon="mdi:comment-text-outline" className="tw-text-xs" /> Remarks
                            </p>
                            <p className="tw-text-xs tw-text-gray-700 tw-mt-0.5">{entry.remarks || '-'}</p>
                        </div>

                        {/* Debits & Credits */}
                        <div className="tw-flex tw-gap-5">
                            <div className="tw-flex-1">
                                <p className="tw-text-[10px] tw-font-semibold tw-text-emerald-700 tw-uppercase tw-tracking-wide tw-mb-2 tw-flex tw-items-center tw-gap-1">
                                    <Icon icon="mdi:arrow-top-right" className="tw-text-xs" /> Debits
                                </p>
                                <div className="tw-space-y-1.5">
                                    {entry.debits?.map((d) => (
                                        <div key={d.id} className="tw-flex tw-items-center tw-justify-between tw-bg-gray-50 tw-rounded tw-px-3 tw-py-2 tw-border tw-border-gray-100">
                                            <div>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{d.account_name}</p>
                                                <p className="tw-text-[10px] tw-text-gray-400">{d.account_cd}</p>
                                            </div>
                                            <span className="tw-text-xs tw-font-semibold tw-text-emerald-600">₹{formatCurrency(d.amount)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="tw-flex-1">
                                <p className="tw-text-[10px] tw-font-semibold tw-text-red-700 tw-uppercase tw-tracking-wide tw-mb-2 tw-flex tw-items-center tw-gap-1">
                                    <Icon icon="mdi:arrow-bottom-left" className="tw-text-xs" /> Credits
                                </p>
                                <div className="tw-space-y-1.5">
                                    {entry.credits?.map((c) => (
                                        <div key={c.id} className="tw-flex tw-items-center tw-justify-between tw-bg-gray-50 tw-rounded tw-px-3 tw-py-2 tw-border tw-border-gray-100">
                                            <div>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{c.account_name}</p>
                                                <p className="tw-text-[10px] tw-text-gray-400">{c.account_cd}</p>
                                            </div>
                                            <span className="tw-text-xs tw-font-semibold tw-text-red-600">₹{formatCurrency(c.amount)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Totals Footer */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-mt-4 tw-pt-3 tw-border-t tw-border-gray-100">
                            <div className="tw-flex tw-items-center tw-gap-4">
                                <div>
                                    <p className="tw-text-[10px] tw-text-gray-600 tw-font-medium tw-uppercase tw-flex tw-items-center tw-gap-1">
                                        <Icon icon="mdi:bank-transfer-in" className="tw-text-xs" /> Total Debit
                                    </p>
                                    <p className="tw-text-sm tw-font-semibold tw-text-emerald-600">₹{formatCurrency(entry.totalDebit)}</p>
                                </div>
                                <div>
                                    <p className="tw-text-[10px] tw-text-gray-600 tw-font-medium tw-uppercase tw-flex tw-items-center tw-gap-1">
                                        <Icon icon="mdi:bank-transfer-out" className="tw-text-xs" /> Total Credit
                                    </p>
                                    <p className="tw-text-sm tw-font-semibold tw-text-red-600">₹{formatCurrency(entry.totalCredit)}</p>
                                </div>
                            </div>
                            <button onClick={onClose} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-gray-100 hover:tw-bg-gray-200">
                                <Icon icon="mdi:close-circle-outline" className="tw-text-sm" />
                                Close
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
                        <p className="tw-text-gray-500 tw-text-sm">Failed to load journal details.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewJournalModal;
